import { JobPresence, JobPresenceTab, JobVisit, User } from "@/types";

// e.g /companies/<companyId>/jobs/<jobId>
const jobPageRegex = new RegExp(/^\/companies\/[^/]+\/jobs\/([^/?#]+)/);

// How far back a visit still counts as "recently here". Much wider than the
// 60s heartbeat on purpose — this is a short history, not a live cursor.
const PRESENCE_WINDOW_MS = 600_000;

const jobTabParamToPresenceTab: Record<string, JobPresenceTab | undefined> = {
  pipeline: JobPresenceTab.PIPELINE,
  "search-v2": JobPresenceTab.SEARCH,
};

const visitKey = (userId: number, jobId: string) =>
  `${userId.toString()}:${jobId}`;

export const presence = {
  // Pulls the job id and tab out of a stored `lastPage` (pathname + search).
  // Returns null when the user isn't on a job page at all.
  parseJobPage: (
    lastPage: string | null | undefined,
  ): { jobId: string; tab: JobPresenceTab } | null => {
    if (!lastPage) return null;

    const [pathname, search] = lastPage.split("?");
    const jobId = jobPageRegex.exec(pathname)?.[1];
    if (!jobId) return null;

    // No `t` param means the job page's default tab, which is the pipeline.
    const tabParam = new URLSearchParams(search ?? "").get("t") ?? "pipeline";
    return {
      jobId,
      tab: jobTabParamToPresenceTab[tabParam] ?? JobPresenceTab.OTHER,
    };
  },

  // Folds the latest poll into the stored history: one entry per (user, job),
  // refreshed when they're seen again, dropped once outside the window. Stays
  // oldest-first so the storage cap trims the stalest entries.
  recordVisits: (
    storedVisits: JobVisit[],
    users: User[] | undefined,
    { excludeUserId, nowMs }: { excludeUserId?: number; nowMs: number },
  ): JobVisit[] => {
    const byKey = new Map<string, JobVisit>();
    storedVisits.forEach((visit) => {
      if (nowMs - visit.lastSeenAtMs >= PRESENCE_WINDOW_MS) return;
      byKey.set(visitKey(visit.userId, visit.jobId), visit);
    });

    (users ?? []).forEach((user) => {
      if (user.id === excludeUserId) return;
      if (user.deletedAt || !user.lastActiveAt) return;

      const parsed = presence.parseJobPage(user.lastPage);
      if (!parsed) return;

      // The heartbeat's own timestamp, so history reflects when they were
      // actually there rather than when this tab happened to poll.
      const lastSeenAtMs = new Date(user.lastActiveAt).getTime();
      if (nowMs - lastSeenAtMs >= PRESENCE_WINDOW_MS) return;

      const key = visitKey(user.id, parsed.jobId);
      const existing = byKey.get(key);
      if (existing && existing.lastSeenAtMs >= lastSeenAtMs) return;

      // Re-inserted at the end so refreshed visits survive the FIFO trim.
      byKey.delete(key);
      byKey.set(key, {
        userId: user.id,
        jobId: parsed.jobId,
        tab: parsed.tab,
        firstSeenAtMs: existing?.firstSeenAtMs ?? lastSeenAtMs,
        lastSeenAtMs,
      });
    });

    return [...byKey.values()];
  },

  // jobId -> everyone seen on that job page inside the window, most recently
  // seen first. Visits whose user has left the roster are skipped.
  byJobId: (
    visits: JobVisit[],
    usersById: Map<number, User>,
    nowMs: number,
  ): Map<string, JobPresence[]> => {
    const map = new Map<string, JobPresence[]>();

    [...visits]
      .sort((a, b) => b.lastSeenAtMs - a.lastSeenAtMs)
      .forEach((visit) => {
        if (nowMs - visit.lastSeenAtMs >= PRESENCE_WINDOW_MS) return;

        const user = usersById.get(visit.userId);
        if (!user) return;

        const existing = map.get(visit.jobId) ?? [];
        existing.push({
          user,
          tab: visit.tab,
          durationMs: visit.lastSeenAtMs - visit.firstSeenAtMs,
        });
        map.set(visit.jobId, existing);
      });

    return map;
  },
};
